"use strict";
/*
 * 8x8 Confidential & Proprietary || Copyright 2025 8x8, Inc. or its affiliates. All Rights Reserved.
 *  _____      _____
 * |  _  |    |  _  |
 *  \ V /__  __\ V /   ___ ___  _ __ ___
 *  / _ \\ \/ // _ \  / __/ _ \| '_ ` _ \
 * | |_| |>  <| |_| || (_| (_) | | | | | |
 * \_____/_/\_\_____(_)___\___/|_| |_| |_|
 *
 * @License https://www.8x8.com/terms-and-conditions/software-development-kit-license-agreement
 * @Author Comanici Paul
 * @Date 2025/08/28
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateFile = validateFile;
const dependencies_1 = require("./dependencies");
const observability_codes_1 = require("./observability.codes");
const config_1 = require("./config");
function validateFile(file, instanceId) {
    const config = (0, config_1.getValidationConfig)();
    const errors = [];
    const warnings = [];
    try {
        // Validate file size
        if (file.size > config.maxFileSize) {
            errors.push(`File size ${formatBytes(file.size)} exceeds maximum allowed size of ${formatBytes(config.maxFileSize)}`);
        }
        // Validate file name length
        if (file.name.length > config.maxFilenameLength) {
            errors.push(`Filename length ${file.name.length} exceeds maximum allowed length of ${config.maxFilenameLength}`);
        }
        // Validate filename for suspicious characters
        // eslint-disable-next-line no-control-regex -- This regex checks for invalid filename characters, including control characters, which is necessary for robust file validation.
        if (/[<>:"|?*\u0000-\u001f]/.test(file.name)) {
            errors.push("Filename contains invalid characters");
        }
        // Get file extension
        const extension = getFileExtension(file.name);
        // Validate file extension
        if (config.allowedExtensions.length > 0 && !config.allowedExtensions.includes(extension)) {
            errors.push(`File extension ${extension} is not allowed`);
        }
        // Validate MIME type
        if (config.allowedMimeTypes.length > 0 && !config.allowedMimeTypes.includes(file.type)) {
            errors.push(`File type ${file.type} is not allowed`);
        }
        // Validate MIME type matches extension (basic check) - security requirement
        const mimeTypeMatchesExtension = doesMimeTypeMatchExtension(file.type, extension);
        if (!mimeTypeMatchesExtension) {
            errors.push(`File extension ${extension} does not match the detected file type ${file.type}. This could indicate a security risk.`);
        }
        // Check for empty files
        if (file.size === 0) {
            warnings.push("File appears to be empty");
        }
        const isValid = errors.length === 0;
        // Log validation result
        (0, dependencies_1.pushLog)(instanceId, [`File validation ${isValid ? "passed" : "failed"} for file: ${file.name}`], {
            context: {
                code: isValid ? observability_codes_1.FILE_CODES.FILE_VALIDATION_PASSED : observability_codes_1.FILE_CODES.FILE_VALIDATION_FAILED,
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                errors: errors.length,
                warnings: warnings.length,
            },
        });
        return {
            isValid,
            errors,
            warnings,
        };
    }
    catch (error) {
        (0, dependencies_1.pushError)(instanceId, error, {
            context: { code: observability_codes_1.FILE_CODES.FILE_VALIDATION_ERROR, fileName: file.name },
        });
        return {
            isValid: false,
            errors: ["File validation failed due to an unexpected error"],
            warnings: [],
        };
    }
}
function getFileExtension(filename) {
    const lastDotIndex = filename.lastIndexOf(".");
    return lastDotIndex === -1 ? "" : filename.substring(lastDotIndex).toLowerCase();
}
function doesMimeTypeMatchExtension(mimeType, extension) {
    const commonMappings = {
        "image/jpeg": [".jpg", ".jpeg"],
        "image/png": [".png"],
        "image/gif": [".gif"],
        "image/webp": [".webp"],
        "image/svg+xml": [".svg"],
        "application/pdf": [".pdf"],
        "text/plain": [".txt"],
        "text/csv": [".csv"],
        "application/json": [".json"],
        "application/xml": [".xml"],
        "text/xml": [".xml"],
        "application/zip": [".zip"],
        "audio/mpeg": [".mp3"],
        "audio/wav": [".wav"],
        "video/mp4": [".mp4"],
    };
    const expectedExtensions = commonMappings[mimeType];
    return !expectedExtensions || expectedExtensions.includes(extension);
}
function formatBytes(bytes) {
    if (bytes === 0)
        return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
//# sourceMappingURL=validator.js.map